import React from 'react';
import { FaRegCalendarAlt } from 'react-icons/fa';

// yyyy-mm-dd in local time, the format every date input and every stored date
// in the app uses (toISOString would shift the day for timezones ahead of UTC)
const toInputDate = (date) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const monthsAgo = (months) => {
  const date = new Date();
  const dayOfMonth = date.getDate();
  // Step back on the 1st so a short target month cannot roll the date forward
  date.setDate(1);
  date.setMonth(date.getMonth() - months);
  const daysInTargetMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  date.setDate(Math.min(dayOfMonth, daysInTargetMonth));
  return date;
};

/** Default range applied wherever graphs are shown: the last 6 months. */
const getDefaultDateRange = () => ({
  startDate: toInputDate(monthsAgo(6)),
  endDate: toInputDate(new Date()),
});

const PRESETS = [
  { label: '1M', months: 1 },
  { label: '3M', months: 3 },
  { label: '6M', months: 6 },
  { label: '1Y', months: 12 },
];

/**
 * Start/end date selector with quick presets, used to scope graph data.
 * `onChange` receives `{ startDate, endDate }` as yyyy-mm-dd strings
 * (empty strings when the range is cleared with "All").
 */
export default function DateRangeFilter({ startDate, endDate, onChange, className = '' }) {
  const applyPreset = (months) =>
    onChange({ startDate: toInputDate(monthsAgo(months)), endDate: toInputDate(new Date()) });

  const isPresetActive = (months) =>
    startDate === toInputDate(monthsAgo(months)) && endDate === toInputDate(new Date());

  const inputClass =
    'rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-800 outline-none transition-all duration-150 hover:border-slate-300 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20';

  return (
    <div
      className={`flex flex-wrap items-end gap-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm ${className}`}
    >
      <div className="flex items-center gap-2 text-slate-500">
        <FaRegCalendarAlt className="h-4 w-4" />
        <span className="text-xs font-semibold uppercase tracking-wide">Date range</span>
      </div>

      <div className="flex flex-col text-left">
        <label htmlFor="graph-start-date" className="mb-1 text-xs font-semibold text-slate-700">
          From
        </label>
        <input
          id="graph-start-date"
          type="date"
          value={startDate || ''}
          max={endDate || undefined}
          onChange={(e) => onChange({ startDate: e.target.value, endDate })}
          className={inputClass}
        />
      </div>

      <div className="flex flex-col text-left">
        <label htmlFor="graph-end-date" className="mb-1 text-xs font-semibold text-slate-700">
          To
        </label>
        <input
          id="graph-end-date"
          type="date"
          value={endDate || ''}
          min={startDate || undefined}
          onChange={(e) => onChange({ startDate, endDate: e.target.value })}
          className={inputClass}
        />
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {PRESETS.map((preset) => (
          <button
            key={preset.label}
            type="button"
            onClick={() => applyPreset(preset.months)}
            className={`rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors ${
              isPresetActive(preset.months)
                ? 'bg-sky-600 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            {preset.label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => onChange({ startDate: '', endDate: '' })}
          className={`rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors ${
            !startDate && !endDate
              ? 'bg-sky-600 text-white'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          All
        </button>
      </div>
    </div>
  );
}

export { getDefaultDateRange, toInputDate };
